import {
  faInfoCircle,
  faPlus,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { useFieldArray, useFormContext } from "react-hook-form";
import slugify from "slugify";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { RequiredAsterisk } from "src/base-components/RequiredAsterisk";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import {
  CaseTemplateFormValues,
  FileCategoryFormValue,
} from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { Tooltip } from "src/design-system/Tooltip";

export const ConfigureFileCategoriesStep: React.FC = () => {
  const step = getStepById("configure-file-categories");
  const form = useFormContext<CaseTemplateFormValues>();
  const { fields, remove, append } = useFieldArray({
    name: "attributes.file_categories",
  });

  const hasPermission = useCheckFieldPermission();

  const addCategory = () => {
    append(
      {
        key: "",
        display_name: "",
        description: "",
        _isNewlyAppended: true,
      } as FileCategoryFormValue,
      {
        focusName: `attributes.file_categories.${fields.length}.display_name`,
      },
    );
  };

  return (
    <FormStepWrapper
      description={step.description}
      docLink={step.docLink}
      title={step.title}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-8 gap-x-2 gap-y-3">
          <FileCategoryRow
            categoryKey={
              <div className="flex items-center gap-1 font-inter-normal-12px text-gray-500">
                Category key <RequiredAsterisk />
                <Tooltip
                  body="Internal name used to reference this category when adding files during case creation.
Use lowercase letters, numbers, and underscores only. Avoid spaces or special characters."
                  placement="top"
                  asChild
                >
                  <Icon color="text-gray-400" icon={faInfoCircle} size="2xs" />
                </Tooltip>
              </div>
            }
            categoryName={
              <div className="font-inter-normal-12px text-gray-500">
                Category name <RequiredAsterisk />
              </div>
            }
            description={
              <div className="font-inter-normal-12px text-gray-500">
                Description
              </div>
            }
          />
          {fields.map((field, index) => {
            const isDefault = isDefaultCategory(
              field as unknown as FileCategoryFormValue,
            );
            const isKeyFieldDisabled =
              isDefault ||
              !hasPermission("edit", `attributes.file_categories.${index}.key`);
            const isNameFieldDisabled =
              isDefault ||
              !hasPermission(
                "edit",
                `attributes.file_categories.${index}.display_name`,
              );
            const isDescriptionFieldDisabled =
              isDefault ||
              !hasPermission(
                "edit",
                `attributes.file_categories.${index}.description`,
              );

            return (
              <FileCategoryRow
                key={field.id}
                categoryKey={
                  <div className="relative">
                    <Input
                      data-loc={`file-category-${index}-key`}
                      placeholder="e.g., category_name"
                      monospaced
                      {...form.register(
                        `attributes.file_categories.${index}.key`,
                      )}
                      disabled={isKeyFieldDisabled}
                      errored={!!getCategoryFieldError(form, index, "key")}
                      fullWidth
                    />
                    {getCategoryFieldError(form, index, "key") && (
                      <ErrorHint>
                        {getCategoryFieldError(form, index, "key")?.message}
                      </ErrorHint>
                    )}
                  </div>
                }
                categoryName={
                  <div className="relative">
                    <Input
                      data-loc={`file-category-${index}-name`}
                      placeholder="Category name"
                      {...form.register(
                        `attributes.file_categories.${index}.display_name`,
                        {
                          onChange: (e) => {
                            const { isDirty } = form.getFieldState(
                              `attributes.file_categories.${index}.key`,
                            );

                            if (!isDirty && !isDefault && !isKeyFieldDisabled) {
                              const slug = slugify(e.target.value, {
                                lower: true,
                                strict: true,
                                replacement: "_",
                              });
                              form.setValue(
                                `attributes.file_categories.${index}.key`,
                                slug,
                              );
                            }
                          },
                          deps: `attributes.file_categories.${index}.key`,
                        },
                      )}
                      disabled={isNameFieldDisabled}
                      errored={
                        !!getCategoryFieldError(form, index, "display_name")
                      }
                      fullWidth
                    />
                    {getCategoryFieldError(form, index, "display_name") && (
                      <ErrorHint>
                        {
                          getCategoryFieldError(form, index, "display_name")
                            ?.message
                        }
                      </ErrorHint>
                    )}
                  </div>
                }
                description={
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        data-loc={`file-category-${index}-description`}
                        placeholder="Add a description for this category"
                        {...form.register(
                          `attributes.file_categories.${index}.description`,
                        )}
                        disabled={isDescriptionFieldDisabled}
                        errored={
                          !!getCategoryFieldError(form, index, "description")
                        }
                        fullWidth
                      />
                      {getCategoryFieldError(form, index, "description") && (
                        <ErrorHint>
                          {
                            getCategoryFieldError(form, index, "description")
                              ?.message
                          }
                        </ErrorHint>
                      )}
                    </div>
                    {!isDefault &&
                      hasPermission("delete", `attributes.file_categories`) && (
                        <Icon
                          color="text-gray-500 hover:text-gray-800"
                          data-loc={`file-category-${index}-remove`}
                          icon={faTrashAlt}
                          size="sm"
                          onClick={() => remove(index)}
                        />
                      )}
                  </div>
                }
              />
            );
          })}
        </div>
        {hasPermission("add", "attributes.file_categories") && (
          <div className="mt-4">
            <Button
              dataLoc="add-file-category-button"
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={addCategory}
            >
              Add a file category
            </Button>
          </div>
        )}
      </div>
    </FormStepWrapper>
  );
};

const FileCategoryRow: React.FC<{
  categoryName: React.ReactNode;
  categoryKey: React.ReactNode;
  description: React.ReactNode;
}> = ({ categoryName, categoryKey, description }) => {
  return (
    <>
      <div className="col-span-2">{categoryName}</div>
      <div className="col-span-2">{categoryKey}</div>
      <div className="col-span-4">{description}</div>
    </>
  );
};

const getCategoryFieldError = (
  form: ReturnType<typeof useFormContext<CaseTemplateFormValues>>,
  index: number,
  field: string,
): { message?: string } | undefined => {
  const error =
    form.formState.errors.attributes?.file_categories?.[index]?.[
      field as keyof FileCategoryFormValue
    ];
  return error && typeof error === "object" && "message" in error
    ? error
    : undefined;
};

const isDefaultCategory = (category: FileCategoryFormValue): boolean => {
  return !!category._isDefault;
};
